import {
  generateId,
  getCurrentUserRecord,
  getDatabase,
  updateDatabase
} from '../data/localDataStore';

const parseOrderDate = (order) => new Date(order?.order_date || order?.created_at);

const filterOrdersByUser = (orders, userId = null) =>
  userId ? orders.filter((order) => order.user_id === userId) : orders;

const isWithinDateRange = (order, startDate, endDate) => {
  const orderDate = parseOrderDate(order);
  if (Number.isNaN(orderDate.getTime())) {
    return false;
  }

  if (startDate && orderDate < new Date(`${startDate}T00:00:00`)) {
    return false;
  }

  if (endDate && orderDate > new Date(`${endDate}T23:59:59.999`)) {
    return false;
  }

  return true;
};

const getCompletedOrders = (orders, startDate, endDate, userId = null) =>
  filterOrdersByUser(orders, userId).filter(
    (order) => order.status === 'completed' && isWithinDateRange(order, startDate, endDate)
  );

const sumProducts = (orders) =>
  orders.reduce(
    (sum, order) =>
      sum +
      (order?.order_data?.products || []).reduce(
        (productSum, product) => productSum + (Number(product?.quantity) || 0),
        0
      ),
    0
  );

const aggregateOrdersByMonth = (orders) =>
  orders.reduce((accumulator, order) => {
    const monthKey = parseOrderDate(order).toISOString().slice(0, 7);
    accumulator[monthKey] = (accumulator[monthKey] || 0) + 1;
    return accumulator;
  }, {});

const aggregateUserActivity = (orders, usersMap) =>
  orders.reduce((accumulator, order) => {
    const username = usersMap.get(order.user_id)?.username || 'utente_locale';
    accumulator[username] = (accumulator[username] || 0) + 1;
    return accumulator;
  }, {});

const aggregateTopProducts = (orders, limit = 10) => {
  const productsMap = new Map();

  orders.forEach((order) => {
    (order?.order_data?.products || []).forEach((product) => {
      const key = product?.sku || product?.name;
      const existing = productsMap.get(key) || {
        product_name: product?.name || key,
        total_quantity: 0,
        total_revenue: 0
      };

      existing.total_quantity += Number(product?.quantity) || 0;
      existing.total_revenue += (Number(product?.quantity) || 0) * (Number(product?.price) || 0);
      productsMap.set(key, existing);
    });
  });

  return Array.from(productsMap.values())
    .sort((left, right) => right.total_quantity - left.total_quantity)
    .slice(0, limit);
};

const calculateGrowthPercentage = (currentValue, previousValue) => {
  if (!previousValue) {
    return currentValue > 0 ? 100 : 0;
  }

  return ((currentValue - previousValue) / previousValue) * 100;
};

const buildComparisonRange = (startDate, endDate) => {
  if (!startDate || !endDate) {
    return null;
  }

  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T23:59:59.999`);
  const diffMs = end.getTime() - start.getTime();

  const previousEnd = new Date(start.getTime() - 1);
  const previousStart = new Date(previousEnd.getTime() - diffMs);

  return {
    startDate: previousStart.toISOString().slice(0, 10),
    endDate: previousEnd.toISOString().slice(0, 10)
  };
};

export const statisticsService = {
  async getAvailableUsers() {
    try {
      const database = getDatabase();
      return {
        success: true,
        users: database.users.map((user) => ({
          id: user.id,
          username: user.username,
          full_name: user.full_name,
          role: user.role,
          is_active: user.is_active
        }))
      };
    } catch (error) {
      return {
        success: false,
        error: 'Errore nel recuperare gli utenti disponibili'
      };
    }
  },

  async getOrdersStatistics(startDate = null, endDate = null, userId = null) {
    try {
      const database = getDatabase();
      const usersMap = new Map(database.users.map((user) => [user.id, user]));
      const orders = getCompletedOrders(database.orders, startDate, endDate, userId);
      const comparisonRange = buildComparisonRange(startDate, endDate);
      const previousOrders = comparisonRange
        ? getCompletedOrders(
            database.orders,
            comparisonRange.startDate,
            comparisonRange.endDate,
            userId
          )
        : [];

      const totalOrders = orders.length;
      const totalRevenue = orders.reduce((sum, order) => sum + (Number(order.total_amount) || 0), 0);
      const totalProducts = sumProducts(orders);
      const previousRevenue = previousOrders.reduce(
        (sum, order) => sum + (Number(order.total_amount) || 0),
        0
      );

      const currentMonths = aggregateOrdersByMonth(orders);
      const monthValues = Object.values(currentMonths);
      const monthlyTrendPercentage =
        monthValues.length >= 2
          ? calculateGrowthPercentage(
              monthValues[monthValues.length - 1],
              monthValues[monthValues.length - 2]
            )
          : totalOrders > 0
            ? 100
            : 0;

      return {
        success: true,
        statistics: {
          totalOrders,
          totalRevenue,
          totalProducts,
          ordersByMonth: currentMonths,
          topProducts: aggregateTopProducts(orders, 10),
          userActivity: aggregateUserActivity(orders, usersMap),
          ordersGrowthPercentage: calculateGrowthPercentage(totalOrders, previousOrders.length),
          revenueGrowthPercentage: calculateGrowthPercentage(totalRevenue, previousRevenue),
          productsGrowthPercentage: calculateGrowthPercentage(
            totalProducts,
            sumProducts(previousOrders)
          ),
          monthlyTrendPercentage
        }
      };
    } catch (error) {
      return {
        success: false,
        error: 'Errore imprevisto nel recuperare le statistiche'
      };
    }
  },

  async addOrder(orderData, status = 'completed') {
    try {
      const currentUser = getCurrentUserRecord();
      const orderId = generateId('order');

      updateDatabase((draft) => {
        draft.orders.unshift({
          id: orderId,
          created_at: new Date().toISOString(),
          order_date: orderData?.orderDate || new Date().toISOString().slice(0, 10),
          total_amount: Number(orderData?.total) || 0,
          status,
          order_data: orderData,
          user_id: currentUser?.id || null,
          source: status === 'printed' ? 'print' : 'whatsapp'
        });
      });

      return { success: true, orderId };
    } catch (error) {
      return { success: false, error: 'Errore nel salvataggio statistiche ordine' };
    }
  },

  async addPrintedOrder(orderData) {
    return this.addOrder(orderData, 'printed');
  },

  async addWhatsAppOrder(orderData) {
    return this.addOrder(orderData, 'completed');
  },

  async diagnoseOrderVisibility(userId = null) {
    try {
      const database = getDatabase();
      const grouped = filterOrdersByUser(database.orders, userId).reduce((accumulator, order) => {
        const bucket = accumulator.get(order.status) || {
          status_name: order.status,
          order_count: 0,
          latest_order: null
        };

        bucket.order_count += 1;
        if (!bucket.latest_order || new Date(order.created_at) > new Date(bucket.latest_order)) {
          bucket.latest_order = order.created_at;
        }

        accumulator.set(order.status, bucket);
        return accumulator;
      }, new Map());

      return { success: true, statusBreakdown: Array.from(grouped.values()) };
    } catch (error) {
      return {
        success: false,
        error: 'Errore imprevisto nella diagnosi ordini'
      };
    }
  },

  async getRecentOrders(limit = 10, userId = null) {
    try {
      const database = getDatabase();
      const usersMap = new Map(database.users.map((user) => [user.id, user]));
      const orders = filterOrdersByUser(database.orders, userId)
        .filter((order) => order.status === 'completed')
        .sort((left, right) => new Date(right.created_at) - new Date(left.created_at))
        .slice(0, limit)
        .map((order) => ({
          ...order,
          user_profiles: usersMap.get(order.user_id)
            ? {
                username: usersMap.get(order.user_id).username,
                full_name: usersMap.get(order.user_id).full_name,
                email: usersMap.get(order.user_id).email
              }
            : null
        }));

      return { success: true, orders };
    } catch (error) {
      return {
        success: false,
        error: 'Errore imprevisto nel recuperare gli ordini recenti'
      };
    }
  },

  async resetAllStatistics() {
    try {
      const currentDatabase = getDatabase();
      const deletedCount = currentDatabase.orders.length;

      updateDatabase((draft) => {
        draft.orders = [];
      });

      return {
        success: true,
        message: 'Statistiche azzerate con successo',
        deletedCount
      };
    } catch (error) {
      return {
        success: false,
        error: 'Errore imprevisto nell\'azzerare le statistiche'
      };
    }
  },

  async deleteOrder(orderId) {
    try {
      let deleted = false;

      updateDatabase((draft) => {
        const originalLength = draft.orders.length;
        draft.orders = draft.orders.filter((order) => order.id !== orderId);
        deleted = draft.orders.length !== originalLength;
      });

      return deleted
        ? { success: true, message: 'Ordine eliminato con successo' }
        : { success: false, error: 'Ordine non trovato' };
    } catch (error) {
      return {
        success: false,
        error: 'Errore imprevisto nell\'eliminare l\'ordine'
      };
    }
  },

  async getTopSellingProducts(startDate = null, endDate = null, limit = 10, userId = null) {
    try {
      const database = getDatabase();
      const orders = getCompletedOrders(database.orders, startDate, endDate, userId);

      return {
        success: true,
        products: aggregateTopProducts(orders, limit)
      };
    } catch (error) {
      return {
        success: false,
        error: 'Errore imprevisto nel recuperare i prodotti più venduti'
      };
    }
  }
};

export default statisticsService;
